import Swiper from "swiper";
import {A11y, Controller, Navigation, Pagination, Parallax} from "swiper/modules";
import {showModal} from "../../../../default/global/js/modal";
import {MediaBackgroundCanvas} from "./MediaBackgroundCanvas";

export function initInspirationFeeds(query = document.querySelectorAll('.js-inspirationFeeds')) {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                new InspirationFeed(entry.target);
                observer.unobserve(entry.target);
            }
        });
    }, {threshold: 0});
    query.forEach(element => observer.observe(element));
}

class InspirationFeed {
    constructor(rootElement) {
        this.rootElement = rootElement;
        this.inspirationFeedId = rootElement.dataset.feedId;
        this.swiperElement = rootElement.querySelector(".swiper");
        this.filterButtons = rootElement.querySelectorAll(".js-inspirationFeedsFilter");
        this.placeholderModalContent = document.querySelector(`.js-inspirationFeedsLightboxContent[data-inspiration-feed-modal-content="${this.inspirationFeedId}"]`);
        this.swiperInstanceMain = this.initMainSwiper();
        this.swiperInstanceModal = null;
        this.imageProgressInterval = null;
        this.initEventListeners();
        if (this.filterButtons.length > 0) {
            const firstFilterId = parseInt(this.filterButtons[0].dataset.filter);
            this.filterSlides(firstFilterId);
        }
    }

    initMainSwiper() {
        const videos = this.swiperElement.querySelectorAll("video");
        videos.forEach(video => {
            video.setAttribute('preload', 'auto');
            video.setAttribute('webkit-playsinline', '');
            video.setAttribute('playsinline', '');
            video.setAttribute('muted', '');
            video.removeAttribute('controls');
            video.setAttribute('loop', 'true');
            let hoverPlayTimeout;
            video.addEventListener("mouseenter", () => {
                hoverPlayTimeout = setTimeout(() => {
                    video.play().catch(console.error);
                }, 50);
            });
            video.addEventListener("mouseleave", () => {
                clearTimeout(hoverPlayTimeout);
                video.pause();
            });
        });
        Swiper.use([Navigation, Parallax, Pagination, Controller,A11y]);
        return new Swiper(this.swiperElement, {
            spaceBetween: 0,
            slidesPerView: 1,
            a11y: {
                slideRole: null,
                slideLabelMessage: null
            },
            navigation: {
                nextEl: this.rootElement.querySelector(".swiper-button-next"),
                prevEl: this.rootElement.querySelector(".swiper-button-prev")
            },
            breakpoints: {
                768: {slidesPerView: 3},
                1024: {slidesPerView: 4},
                1250: {slidesPerView: 4},
                1540: {slidesPerView: 5}
            }
        });
    }

    initEventListeners() {
        this.filterButtons.forEach((button) => {
            button.addEventListener("click", () => {
                const filterId = parseInt(button.dataset.filter);
                this.filterSlides(filterId);
            });
        });
        const handleSlideInteraction = (event) => {
            const clickedSlide = event.target.closest(".js-inspirationFeedsItem");
            if (!clickedSlide) return;

            let clickedIndex = -1;
            for (let idx = 0; idx < this.swiperInstanceMain.slides.length; idx++) {
                const slide = this.swiperInstanceMain.slides[idx];
                if (!slide.classList.contains("inspiration-feed-hidden")) {
                    clickedIndex++;
                }
                if (slide === clickedSlide) break;
            }

            this.placeholderModalContent.innerHTML = "";
            const clonedSwiper = this.swiperElement.cloneNode(true);
            this.placeholderModalContent.appendChild(clonedSwiper);
            requestAnimationFrame(() => {
                showModal(document.getElementById("inspirationFeed-modal"), () => {
                    this.swiperInstanceModal = this.initModalSwiper(
                        this.placeholderModalContent.querySelector(".swiper"),
                        clickedIndex
                    );
                });
            });
        };

        this.rootElement.addEventListener("click", handleSlideInteraction);

        this.rootElement.addEventListener("keydown", (event) => {
            if (event.key === "Enter") {
                const focusedSlide = document.activeElement.closest(".js-inspirationFeedsItem");
                if (focusedSlide) {
                    event.preventDefault(); // optional, prevents scroll or other side effects
                    handleSlideInteraction({ target: focusedSlide });
                }
            }
        });
    }

    filterSlides(id) {
        this.filterButtons.forEach((btn) => {
            const isActive = btn.dataset.filter === id.toString();
            btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
        const activeClass = `collection_group_${id}`;
        this.swiperInstanceMain.slides.forEach((slide) => {
            slide.classList.toggle('inspiration-feed-hidden', !slide.classList.contains(activeClass));
        });
        this.swiperInstanceMain.update();
    }

    initModalSwiper(swiperElement, clickedIndex) {
        const mediaBackgroundCanvasElement = document.querySelector(".js-inspirationFeedsModalBackgroundCanvas");
        const paginationElement = swiperElement.querySelector(".swiper-pagination");
        const hiddenSlides = swiperElement.querySelectorAll('.inspiration-feed-hidden');
        /**
         * Remove hidden slides from dom
         */
        hiddenSlides.forEach(el => el.remove());

        const handleTransitionStart = (swiper) => {
            this.resetPaginationState(paginationElement, swiper);
            if (this.imageProgressInterval) {
                clearInterval(this.imageProgressInterval);
                this.imageProgressInterval = null;
            }
            swiper.slides.forEach((slide) => {
                const video = slide.querySelector('video');
                if (video) {
                    const clone = video.cloneNode(true);
                    video.parentNode.replaceChild(clone, video);
                    clone.currentTime = 0;
                }
            });
        };

        const handleTransitionEnd = (swiper) => {
            this.setPaginationBulletToVisited(paginationElement, swiper);

            swiper.slides.forEach((slide) => {
                const video = slide.querySelector('video');
                if (video) {
                    const clone = video.cloneNode(true);
                    video.parentNode.replaceChild(clone, video);
                    clone.currentTime = 0;
                }
            });
            const currentSlide = swiper.slides[swiper.activeIndex];
            const video = currentSlide.querySelector('video');
            const img = currentSlide.querySelector('img');
            if (video || img) {
                new MediaBackgroundCanvas(video ?? img, mediaBackgroundCanvasElement);
            }
            if (video) {
                video.muted = true;
                video.loop = false;

                // ✅ Delay play to avoid AbortError
                setTimeout(() => {
                    if (document.body.contains(video)) {
                        video.play().catch(console.warn);
                       this.setupVideoHandler(video, swiper, paginationElement);
                    }
                }, 50);
            }
            if (img) {
                this.setupImageHandler(swiper, paginationElement);
            }
        };

        Swiper.use([Navigation, Parallax, Pagination, Controller,A11y]);
        return new Swiper(swiperElement, {
            initialSlide: clickedIndex,
            spaceBetween: 0,
            slidesPerView: 1,
            parallax: true,
            pagination: {
                el: paginationElement,
                clickable: true
            },
            a11y: {
                slideRole: null,
                slideLabelMessage: null
            },
            navigation: {
                nextEl: swiperElement.querySelector(".swiper-button-next"),
                prevEl: swiperElement.querySelector(".swiper-button-prev")
            },
            on: {
                init() {
                    handleTransitionEnd(this);
                },
                transitionStart() {
                    handleTransitionStart(this);
                },
                transitionEnd() {
                    handleTransitionEnd(this);
                    this.swiperInstanceMain?.slideTo(this.activeIndex);
                }
            }
        });
    }

    setPaginationBulletToVisited(paginationElement, swiper) {
        const bullets = paginationElement.querySelectorAll('.swiper-pagination-bullet');
        bullets.forEach((bullet, index) => {
            const visited = index < swiper.activeIndex;
            bullet.classList.toggle("visited", visited);
            bullet.style.setProperty('--progress', visited ? '0' : '0');
        });
    }

    resetPaginationState(paginationElement) {
        const bullets = paginationElement.querySelectorAll('.swiper-pagination-bullet');
        bullets.forEach((bullet) => {
            bullet.style.setProperty('--progress', '0');
        });
    }

    setupImageHandler(swiper, paginationElement) {
        if (this.imageProgressInterval) {
            clearInterval(this.imageProgressInterval);
            this.imageProgressInterval = null;
        }
        const bullet = paginationElement.querySelectorAll('.swiper-pagination-bullet')[swiper.activeIndex];
        let progress = 0;
        this.imageProgressInterval = setInterval(() => {
            progress += 0.001;
            bullet.style.setProperty('--progress', progress);
            if (progress > 1) {
                swiper.slideNext();
                clearInterval(this.imageProgressInterval);
                this.imageProgressInterval = null;
            }
        }, 1);
    }

    setupVideoHandler(video, swiper, paginationElement) {
        const bullet = paginationElement.querySelectorAll('.swiper-pagination-bullet')[swiper.activeIndex];
        let animationFrameId;
        const updateProgress = () => {
            if (video.paused) {
                bullet?.style.setProperty('--progress', 0);
            } else if (video.duration > 0) {
                const progress = video.currentTime / video.duration;
                bullet?.style.setProperty('--progress', progress);
            }
            // Schedule next frame
            animationFrameId = requestAnimationFrame(updateProgress);
        };
        video.currentTime = 0;
        // ✅ Delay video.play to ensure element is in DOM
        setTimeout(() => {
            if (document.body.contains(video)) {
                video.play().catch(console.warn);
                updateProgress(); // Start the animation loop
            }
        }, 50);
        video.addEventListener('ended', () => {
            cancelAnimationFrame(animationFrameId); // Stop the animation loop
            if (swiper.activeIndex < swiper.slides.length - 1) {
                swiper.slideNext();
            }
        });
    }
}
